import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';
import translationService from '../services/translationService';
import { Languages } from 'lucide-react';

const LanguageContext = createContext();

export const useLanguages = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguages must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [languages, setLanguages] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState('ro');
  const [currency, setCurrency] = useState({ symbol: '$', code: 'USD' });
  const [isLoading, setIsLoading] = useState(true);

  // Load languages and currency from settings
  useEffect(() => {
    const loadSettings = async () => {
      // Skip loading settings if no auth token (on login page)
      const token = localStorage.getItem('admin_token');
      if (!token) {
        // Use default settings without making API call
        setLanguages([
          { code: 'ro', name: 'Romanian', flag: '🇷🇴', is_default: true },
          { code: 'ru', name: 'Russian', flag: '🇷🇺', is_default: false }
        ]);
        setCurrency({ symbol: 'MDL', code: 'MDL' });
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get('/settings');
        const apiSettings = response.data.data;
        
        // Load default language first
        const defaultLang = apiSettings.default_language || 'en';
        setDefaultLanguage(defaultLang);

        // Load ALL available languages for admin dashboard
        if (apiSettings.available_languages && apiSettings.available_languages.length > 0) {
          // Ensure we have proper language objects with all required fields
          const fixedLanguages = apiSettings.available_languages.map(lang => {
            // Handle both string (code only) and object formats
            if (typeof lang === 'string') {
              return {
                code: lang,
                name: lang === 'en' ? 'English' : lang === 'ro' ? 'Romanian' : lang === 'ru' ? 'Russian' : lang,
                flag: lang === 'en' ? '🇺🇸' : lang === 'ro' ? '🇷🇴' : lang === 'ru' ? '🇷🇺' : '🏳️',
                is_default: lang === defaultLang
              };
            } else {
              // It's already an object, ensure it has all required fields
              return {
                code: lang.code || lang,
                name: lang.name || (lang.code === 'en' ? 'English' : lang.code === 'ro' ? 'Romanian' : lang.code === 'ru' ? 'Russian' : lang.code),
                flag: lang.flag || (lang.code === 'en' ? '🇺🇸' : lang.code === 'ro' ? '🇷🇴' : lang.code === 'ru' ? '🇷🇺' : '🏳️'),
                is_default: lang.is_default || lang.code === defaultLang
              };
            }
          });
          // console.log('🌍 Loaded languages from API:', fixedLanguages);
          setLanguages(fixedLanguages);
        } else {
          // Default languages if not set
          setLanguages([
            { code: 'en', name: 'English', flag: '🇺🇸', is_default: true },
            { code: 'ro', name: 'Romanian', flag: '🇷🇴', is_default: false },
            { code: 'ru', name: 'Russian', flag: '🇷🇺', is_default: false }
          ]);
        }

        // Load currency - handle both string and object formats
        if (apiSettings.currency) {
          if (typeof apiSettings.currency === 'string') {
            setCurrency({ symbol: apiSettings.currency, code: apiSettings.currency });
          } else {
            setCurrency(apiSettings.currency);
          }
        } else {
          // Default currency if not set
          setCurrency({ symbol: 'MDL', code: 'MDL' });
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        // Fallback to default settings
        setLanguages([
          { code: 'en', name: 'English', flag: '🇺🇸', is_default: true },
          { code: 'ro', name: 'Romanian', flag: '🇷🇴', is_default: false },
          { code: 'ru', name: 'Russian', flag: '🇷🇺', is_default: false }
        ]);
        setCurrency({ symbol: 'MDL', code: 'MDL' });
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Auto-translate function
  const autoTranslate = async (defaultText, currentTranslations = {}) => {
    if (!defaultText || !defaultText.trim()) {
      return currentTranslations;
    }

    try {
      const targetLanguages = languages
        .filter(lang => lang.code !== defaultLanguage)
        .map(lang => lang.code);

      const translations = await translationService.translateToMultipleLanguages(
        defaultText,
        targetLanguages,
        defaultLanguage
      );

      // Merge with existing translations, don't overwrite existing values
      const result = { ...currentTranslations };
      Object.keys(translations).forEach(lang => {
        if (!result[lang] || result[lang].trim() === '') {
          result[lang] = translations[lang];
        }
      });

      return result;
    } catch (error) {
      console.error('Auto-translation failed:', error);
      return currentTranslations;
    }
  };

  const value = {
    languages,
    defaultLanguage,
    currency,
    isLoading,
    autoTranslate
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Helper component to render translation inputs
export const TranslationInputs = ({ 
  label,
  value = {}, 
  onChange, 
  onAutoTranslate,
  isTranslating = false,
  type = 'text',
  placeholder = '',
  required = false,
  rows = 4,
  textarea = false
}) => {
  const { languages, defaultLanguage } = useLanguages();

  // Ensure value is an object (handle JSONB data from backend)
  const normalizedValue = typeof value === 'object' && value !== null ? value : {};

  const handleInputChange = (langCode, newValue) => {
    onChange({
      ...normalizedValue,
      [langCode]: newValue
    });
  };

  const renderInput = (lang) => {
    const commonProps = {
      value: normalizedValue[lang.code] || '',
      onChange: (e) => handleInputChange(lang.code, e.target.value),
      className: "input-field w-full",
      placeholder: `${placeholder} (${lang.name})`,
      required: required && lang.code === defaultLanguage
    };

    if (textarea || type === 'textarea') {
      return <textarea {...commonProps} rows={rows} />;
    }
    
    return <input type={type} {...commonProps} />;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {onAutoTranslate && (
          <button
            type="button"
            onClick={onAutoTranslate}
            disabled={isTranslating || !normalizedValue[defaultLanguage]}
            className="btn-secondary text-xs flex items-center space-x-1"
          >
            {isTranslating ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
                <span>Translating...</span>
              </>
            ) : (
              <>
                <Languages className="h-3 w-3" />
                <span>Auto Translate</span>
              </>
            )}
          </button>
        )}
      </div>

      {textarea || type === 'textarea' ? (
        <div className="space-y-4">
          {languages.map((lang) => (
            <div key={lang.code}>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                {lang.name} {lang.code === defaultLanguage && '(Default)'}
              </label>
              {renderInput(lang)}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {languages.map((lang) => (
            <div key={lang.code}>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                {lang.name} {lang.code === defaultLanguage && '(Default)'}
              </label>
              {renderInput(lang)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper component to render translation display
export const TranslationDisplay = ({ value = {}, showAll = false }) => {
  const { languages, defaultLanguage } = useLanguages();

  // Ensure value is an object (handle JSONB data from backend)
  const normalizedValue = typeof value === 'object' && value !== null ? value : {};

  if (!normalizedValue || Object.keys(normalizedValue).length === 0) {
    return <span className="text-gray-500">No translation</span>;
  }

  if (!showAll) {
    // Show only the default language value
    const displayValue = normalizedValue[defaultLanguage] || Object.values(normalizedValue)[0] || 'No translation';
    return <span>{displayValue}</span>;
  }

  // Show all translations
  return (
    <div className="space-y-1">
      {languages.map((lang) => (
        <div key={lang.code} className="flex items-center space-x-2">
          <span className="text-xs text-gray-500 w-8">{lang.code}:</span>
          <span>{normalizedValue[lang.code] || 'N/A'}</span>
        </div>
      ))}
    </div>
  );
};

// Helper function to format price with currency
export const formatPrice = (price, currency) => {
  if (price === null || price === undefined) return '';
  
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  if (isNaN(numericPrice)) return '';
  
  // Format with 2 decimal places and currency symbol
  return `${currency?.symbol || ''}${numericPrice.toFixed(2)}`;
};

// Helper component to display price with currency
export const PriceDisplay = ({ price }) => {
  const { currency } = useLanguages();
  
  return (
    <span>{formatPrice(price, currency)}</span>
  );
};

